import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Envelope, PaperPlaneTilt, X } from '@phosphor-icons/react';

import { quickSendMemberEmail } from '@/api/broadcasts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn } from '@/lib/utils';

// QuickEmailDialog is the Members page "Actions → email" surface: a quick
// subject + free-text message sent to the selected members, wrapped in the
// club's branded header/footer server-side. This is deliberately NOT the
// block-based broadcast composer — it's the one-off "tell these people one
// thing" path. Progress is reported on the notification bell, so on success
// the dialog just closes.

export interface QuickEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  token: string | null;
  memberIds: string[];
  recipientCount: number;
  onSent: () => void;
}

export function QuickEmailDialog({
  open,
  onOpenChange,
  slug,
  token,
  memberIds,
  recipientCount,
  onSent,
}: QuickEmailDialogProps) {
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // Reset the form each time the dialog opens so a previous (sent) draft
  // doesn't linger into the next selection.
  useEffect(() => {
    if (open) {
      setSubject('');
      setBody('');
    }
  }, [open]);

  const sendMutation = useMutation({
    mutationFn: () =>
      quickSendMemberEmail(token, slug, {
        subject: subject.trim(),
        body,
        member_ids: memberIds,
      }),
    onSuccess: () => {
      onSent();
      onOpenChange(false);
    },
  });

  const canSend =
    subject.trim().length > 0 && body.trim().length > 0 && memberIds.length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSend || sendMutation.isPending) return;
    const ok = await confirm({
      title: t('members.email.confirmTitle', { count: recipientCount }),
      body: t('members.email.confirmBody', { count: recipientCount }),
      confirmLabel: t('members.email.sendCta'),
    });
    if (ok) sendMutation.mutate();
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-foreground/40" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[81] flex max-h-[88vh] w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl"
        >
          <header className="flex items-start gap-3 border-b border-border px-6 py-4">
            <span
              aria-hidden
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/70"
            >
              <Envelope size={18} weight="duotone" />
            </span>
            <div className="flex-1 overflow-hidden">
              <DialogPrimitive.Title className="font-heading text-lg font-bold leading-tight">
                {t('members.email.title')}
              </DialogPrimitive.Title>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {t('members.email.recipients', { count: recipientCount })}
              </p>
            </div>
            <DialogPrimitive.Close asChild>
              <Button type="button" variant="ghost" size="icon" aria-label={t('common.cancel')}>
                <X weight="bold" />
              </Button>
            </DialogPrimitive.Close>
          </header>

          <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
              <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
                {t('members.email.subjectLabel')}
                <Input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                  autoFocus
                  autoComplete="off"
                  placeholder={t('members.email.subjectPlaceholder')}
                />
              </label>

              <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
                {t('members.email.messageLabel')}
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={5000}
                  rows={8}
                  className={cn(
                    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
                    'ring-offset-background placeholder:text-muted-foreground',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                  )}
                  placeholder={t('members.email.messagePlaceholder')}
                />
              </label>

              <p className="text-xs text-muted-foreground">
                {t('members.email.chromeHint')}
              </p>

              {sendMutation.isError && (
                <p className="text-sm text-destructive">{t('members.email.error')}</p>
              )}
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-border px-6 py-3">
              <DialogPrimitive.Close asChild>
                <Button type="button" variant="outline">
                  {t('common.cancel')}
                </Button>
              </DialogPrimitive.Close>
              <Button type="submit" disabled={!canSend || sendMutation.isPending}>
                <PaperPlaneTilt weight="bold" />
                {sendMutation.isPending
                  ? t('members.email.sending')
                  : t('members.email.sendCta')}
              </Button>
            </footer>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
